// src/admin/pages/Dashboard/components/orders/InProgressTablesView.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

/**
 * InProgressTablesView
 * ------------------------------------------------------------
 * - Stati item selezionabili: ["in_progress", "ready"]
 * - PATCH status piatto:   /kitchen/order_items/:itemId/status
 * - PATCH consegna piatto: /orders/:orderId/items/:itemId/deliver
 * ------------------------------------------------------------
 */
export default function InProgressTablesView({
  orders,
  refreshOrders = () => {},
}) {
  /* ------------------------------------------------------------------ */
  /*  STATE & EFFECTS                                                   */
  /* ------------------------------------------------------------------ */
  const [expanded, setExpanded] = useState({});
  const [localOrders, setLocalOrders] = useState([]);

  useEffect(() => {
    setLocalOrders(orders || []);
  }, [orders]);

  /* ------------------------------------------------------------------ */
  /*  MEMOIZED / CALLBACK VALUES (tutti i Hooks prima di qualsiasi return) */
  /* ------------------------------------------------------------------ */
  const grouped = useMemo(
    () => groupOrdersByTableSession(localOrders),
    [localOrders]
  );

  const toggleExpand = useCallback((key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleItemStatusChange = useCallback(
    async (orderId, itemId, newStatus) => {
      // 1 • optimistic UI
      setLocalOrders((prev) =>
        prev.map((o) =>
          o.id !== orderId
            ? o
            : {
                ...o,
                items: o.items.map((it) =>
                  it.id === itemId ? { ...it, status: newStatus } : it
                ),
              }
        )
      );

      // 2 • API call (aggiunto token)
      try {
        const token = localStorage.getItem("access_token") || "";
        const url = `${process.env.REACT_APP_API_URL}/kitchen/order_items/${itemId}/status`;
        await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        });
      } catch (err) {
        console.error("handleItemStatusChange:", err);
      } finally {
        await refreshOrders();
      }
    },
    [refreshOrders]
  );

  const handleDeliverOrder = useCallback(
    async (order) => {
      // 1 • optimistic remove
      setLocalOrders((prev) => prev.filter((o) => o.id !== order.id));

      try {
        const token = localStorage.getItem("access_token") || "";
        const readyItems =
          order.items?.filter((it) => it.status === "ready") || [];

        for (const item of readyItems) {
          const url = `${process.env.REACT_APP_API_URL}/orders/${order.id}/items/${item.id}/deliver`;
          await fetch(url, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        }
      } catch (err) {
        console.error("handleDeliverOrder:", err);
      } finally {
        await refreshOrders();
      }
    },
    [refreshOrders]
  );

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                             */
  /* ------------------------------------------------------------------ */
  const isEmpty = !localOrders || localOrders.length === 0;

  return (
    <div>
      {isEmpty && <div style={styles.noData}>Nessun ordine in corso.</div>}

      {!isEmpty &&
        Object.keys(grouped).map((key) => {
          const [tableNumber, sessionId] = key.split("_");
          const list = grouped[key];
          const sessionTotal = list.reduce(
            (acc, o) => acc + getOrderTotal(o),
            0
          );
          const isOpen = !!expanded[key];

          return (
            <div key={key} style={styles.card}>
              {/* HEADER CARD */}
              <div
                style={styles.cardHeader}
                onClick={() => toggleExpand(key)}
              >
                <strong>Tavolo {tableNumber}</strong>
                <span>Sessione #{sessionId}</span>
              </div>

              <div style={styles.topRow}>
                <span style={styles.totalText}>
                  Totale sessione: € {sessionTotal.toFixed(2)}
                </span>
                <button
                  style={styles.toggleButton}
                  onClick={() => toggleExpand(key)}
                >
                  {isOpen ? "Nascondi" : "Dettagli"}
                </button>
              </div>

              {/* CONTENUTO ESPANDIBILE */}
              {isOpen && (
                <div style={styles.cardContent}>
                  {list.map((order) => {
                    const subTot = getOrderTotal(order);
                    const allItemsReady =
                      order.items?.length > 0 &&
                      order.items.every((it) => it.status === "ready");

                    return (
                      <div key={order.id} style={styles.orderBlock}>
                        <div style={styles.orderBlockHeader}>
                          Ordine #{order.id} – {order.status}
                        </div>

                        {/* ITEMS */}
                        {order.items?.map((item) => (
                          <ItemRow
                            key={item.id}
                            orderId={order.id}
                            item={item}
                            onChangeStatus={handleItemStatusChange}
                          />
                        ))}

                        <div style={styles.orderBlockTotal}>
                          Subtotale Ordine: € {subTot.toFixed(2)}
                        </div>

                        {allItemsReady && (
                          <button
                            style={styles.deliverOrderButton}
                            onClick={() => handleDeliverOrder(order)}
                          >
                            Consegna Ordine
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* TOTALE SESSIONE */}
                  <hr />
                  <div style={styles.sessionTotal}>
                    Totale sessione:&nbsp;
                    <strong>€ {sessionTotal.toFixed(2)}</strong>
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  COMPONENTE FIGLIO                                                  */
/* ------------------------------------------------------------------ */
function ItemRow({ item, orderId, onChangeStatus }) {
  const allowedStates = ["in_progress", "ready"];
  const handleChange = (e) =>
    onChangeStatus(orderId, item.id, e.target.value);

  return (
    <div style={{ marginBottom: "4px" }}>
      {item.menu_item_name} x {item.quantity} → €{" "}
      {(item.menu_item_price * item.quantity).toFixed(2)}
      <br />
      <label>Stato:&nbsp;</label>
      <select value={item.status} onChange={handleChange}>
        {allowedStates.map((st) => (
          <option key={st} value={st}>
            {st}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */
function groupOrdersByTableSession(orders) {
  return orders.reduce((acc, o) => {
    const key = `${o.table_number || "??"}_${o.table_session_id || "??"}`;
    acc[key] = acc[key] ? [...acc[key], o] : [o];
    return acc;
  }, {});
}

function getOrderTotal(order) {
  return (order.items || []).reduce(
    (sum, it) =>
      sum + (it.menu_item_price || 0) * (it.quantity || 0),
    0
  );
}

/* ------------------------------------------------------------------ */
/*  STILI (inline per brevità)                                         */
/* ------------------------------------------------------------------ */
const styles = {
  noData: {
    padding: "1rem",
    textAlign: "center",
    color: "#333",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginBottom: "1rem",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    padding: "1rem",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
    color: "#FF6600",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.5rem",
    backgroundColor: "#f7f7f7",
    padding: "6px 8px",
    borderRadius: "4px",
  },
  totalText: {
    fontWeight: "bold",
  },
  toggleButton: {
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    color: "#333",
  },
  cardContent: {
    marginTop: "0.5rem",
  },
  orderBlock: {
    marginBottom: "0.75rem",
    padding: "0.5rem",
    borderRadius: "4px",
    backgroundColor: "#FAFAFA",
  },
  orderBlockHeader: {
    fontWeight: "bold",
    marginBottom: "0.3rem",
    color: "#444",
  },
  orderBlockTotal: {
    textAlign: "right",
    fontStyle: "italic",
    marginTop: "0.3rem",
    marginBottom: "0.3rem",
  },
  deliverOrderButton: {
    backgroundColor: "#FFC107",
    border: "none",
    padding: "6px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  sessionTotal: {
    textAlign: "right",
    fontWeight: "bold",
    fontSize: "1rem",
  },
};
